import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Container, 
  Card, 
  Form, 
  Button, 
  Alert, 
  Row, 
  Col, 
  Spinner, 
  Badge,
  ProgressBar,
  Tabs,
  Tab,
  ListGroup
} from 'react-bootstrap';
import { 
  FaDownload, 
  FaFileAlt, 
  FaArrowLeft, 
  FaInfoCircle, 
  FaCheckCircle, 
  FaExclamationTriangle,
  FaUniversity,
  FaBuilding,
  FaFileExport,
  FaCog,
  FaCloudUploadAlt,
  FaEye,
  FaTrashAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import { generarAFPNet } from '../../services/planilla/generarAFPNet';
import { generarArchivosPLAME } from '../../services/planilla/generarArchivosPLAME';
import { generarTRegistro } from '../../services/planilla/generarTRegistro';
import { generarPagosInterbank } from '../../services/planilla/generarPagosInterbank';
import { generarPagosBCP } from '../../services/planilla/generarPagosBCP';
import { generarPagosBBVA } from '../../services/planilla/generarPagosBBVA';
import { useNavigate, useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import firebaseApp from '../../firebase/credenciales';

const storage = getStorage(firebaseApp);
const db = getFirestore(firebaseApp);
const TIPOS_REQUIEREN_PLANILLA = new Set(['afpnet', 'plame', 'tregistro', 'interbank', 'bcp', 'bbva']);
const MOTIVOS_BAJA_TREGISTRO = [
  { code: '01', label: 'Renuncia voluntaria' },
  { code: '02', label: 'Despido / Retiro' },
  { code: '03', label: 'Fin de contrato' },
  { code: '04', label: 'Jubilación' },
  { code: '05', label: 'Fallecimiento' },
  { code: '99', label: 'Otro motivo' },
];

const ExportacionArchivos = ({ usuario, tipoExportacionInicial = '' }) => {
  const { planillaId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [tipoExportacion, setTipoExportacion] = useState(tipoExportacionInicial || '');
  const [resultado, setResultado] = useState(null);
  const [empleados, setEmpleados] = useState([]);
  const [empleadosSeleccionados, setEmpleadosSeleccionados] = useState([]);
  const [operacionesTRegistro, setOperacionesTRegistro] = useState({}); // Mapa { id: 'ALTA' | 'BAJA' | 'MODIFICAR' }
  const [motivosBajaTRegistro, setMotivosBajaTRegistro] = useState({}); // Mapa { id: '01'|'02'|... }
  const [loadingEmpleados, setLoadingEmpleados] = useState(false);
  const [planilla, setPlanilla] = useState(null);
  const [activeTab, setActiveTab] = useState('exportar');
  const [uploading, setUploading] = useState({}); // { [tipo]: progreso }
  const autoExportKeyRef = useRef('');

  const parseFecha = (value) => {
    if (!value) return null;
    if (value?.seconds) return new Date(value.seconds * 1000);
    if (value instanceof Date) return value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const periodoFromDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  };

  // Actualizar tipoExportacion cuando cambie tipoExportacionInicial
  const handleExportacion = useCallback(async (tipoForzado = '') => {
    const tipoActual = typeof tipoForzado === 'string' && tipoForzado ? tipoForzado : tipoExportacion;
    try {
      setLoading(true);
      setResultado(null);

      if (!tipoActual) {
        setResultado({
          success: false,
          mensaje: "Debe seleccionar un tipo de exportación."
        });
        return;
      }
      
      if (TIPOS_REQUIEREN_PLANILLA.has(tipoActual) && !planillaId) {
        setResultado({
          success: false,
          mensaje: `Debe seleccionar una planilla para generar archivos ${tipoActual.toUpperCase()}`
        });
        return;
      }

      if (tipoActual === 'afpnet' || tipoActual === 'plame') {
        if (!planillaId) {
          setResultado({
            success: false,
            mensaje: `Debe seleccionar una planilla para generar archivos ${tipoActual.toUpperCase()}`
          });
          return;
        }

        // VALIDACIÓN DE CONSISTENCIA (Requerimiento Regulatorio)
        const erroresConsistencia = [];
        
        // Obtener IDs de empleados en esta planilla específica
        const qDetalle = query(collection(db, "pl_planilla_detalle"), where("planilla_id", "==", planillaId));
        const snapDetalle = await getDocs(qDetalle);
        const idsEnPlanilla = snapDetalle.docs.map(doc => String(doc.data().empleado_id || "")).filter(id => !!id);
        const idsSet = new Set(idsEnPlanilla);
        
        // Filtrar la lista maestra de empleados para validar solo los que están en la planilla
        const detalleValidar = empleados.filter(e => idsSet.has(String(e.id)) || idsSet.has(String(e.dni || "")));
        
        detalleValidar.forEach(d => {
          if (!d.regimen_pensionario || d.regimen_pensionario === 'NO ASIGNADO') {
            erroresConsistencia.push(`${d.nombres}: Sin Régimen Pensionario asignado`);
          }
          // Para AFPNET solo validamos CUSPP si el trabajador está en un régimen de AFP
          if (tipoActual === 'afpnet' && d.regimen_pensionario?.toUpperCase().includes('AFP')) {
            if (!d.cuspp || d.cuspp.length < 12) {
              erroresConsistencia.push(`${d.nombres}: CUSPP inválido o faltante (Requerido 12 dígitos)`);
            }
          }
        });

        if (erroresConsistencia.length > 0) {
          setResultado({
            success: false,
            mensaje: "Error de Consistencia T-Registro vs PLAME/AFPNet",
            detalles: erroresConsistencia
          });
          return;
        }
      }

      if (tipoActual === 'afpnet') {
        const result = await generarAFPNet(planillaId);
        setResultado({
          success: true,
          mensaje: result.message
        });
      } 
      else if (tipoActual === 'plame') {
        const result = await generarArchivosPLAME(planillaId);
        setResultado({
          success: true,
          mensaje: result.message || "Archivos PLAME generados correctamente: REM y JOR"
        });
      }
      else if (tipoActual === 'tregistro') {
        if (!empleadosSeleccionados.length) {
          setResultado({
            success: false,
            mensaje: "Debe seleccionar al menos un trabajador para exportar T-Registro.",
          });
          return;
        }

        const bajasSinMotivo = empleadosSeleccionados.filter((id) =>
          operacionesTRegistro[id] === 'BAJA' && !motivosBajaTRegistro[id]
        );
        if (bajasSinMotivo.length > 0) {
          setResultado({
            success: false,
            mensaje: "Hay trabajadores en BAJA sin motivo seleccionado.",
          });
          return;
        }

        const result = await generarTRegistro(
          usuario.ruc,
          new Date(),
          planillaId,
          empleadosSeleccionados,
          operacionesTRegistro,
          motivosBajaTRegistro
        );
        setResultado({
          success: true,
          mensaje: result.message || "ZIP T-Registro generado correctamente (incluye .ide, .tri y .per)"
        });
      }
      else if (tipoActual === 'interbank') {
        const result = await generarPagosInterbank(planillaId);
        setResultado({
          success: true,
          mensaje: result.message
        });
      }
      else if (tipoActual === 'bcp') {
        const result = await generarPagosBCP(planillaId);
        setResultado({
          success: true,
          mensaje: result.message
        });
      }
      else if (tipoActual === 'bbva') {
        const result = await generarPagosBBVA(planillaId);
        setResultado({
          success: true,
          mensaje: result.message
        });
      }
    } catch (error) {
      console.error("Error en exportación:", error);
      setResultado({
        success: false,
        mensaje: error.message || "Error al procesar la exportación"
      });
    } finally {
      setLoading(false);
    }
  }, [
    tipoExportacion,
    planillaId,
    empleados,
    usuario.ruc,
    empleadosSeleccionados,
    operacionesTRegistro,
    motivosBajaTRegistro,
  ]);

  useEffect(() => {
    if (!tipoExportacionInicial) return;
    setTipoExportacion(tipoExportacionInicial);

    if (!planillaId) return;
    const key = `${tipoExportacionInicial}|${planillaId}`;
    if (autoExportKeyRef.current === key) return;
    autoExportKeyRef.current = key;
    handleExportacion(tipoExportacionInicial);
  }, [tipoExportacionInicial, planillaId, handleExportacion]);

  useEffect(() => {
    const cargarDatos = async () => {
      if (!usuario?.ruc) return;
      setLoadingEmpleados(true);
      try {
        // 1. Cargar datos de la planilla si existe
        if (planillaId) {
          const planillaDoc = await getDoc(doc(db, "pl_planillas", planillaId));
          if (planillaDoc.exists()) {
            setPlanilla(planillaDoc.data());
          }
        }

        // 2. Cargar todos los empleados de la empresa (Activos y Cesados)
        const q = query(
          collection(db, "pl_empleados"),
          where("ruc_empresa", "==", usuario.ruc)
        );
        const snapshot = await getDocs(q);
        const empList = [];
        snapshot.forEach(doc => empList.push({ id: doc.id, ...doc.data() }));
        setEmpleados(empList);
      } catch (error) {
        console.error("Error cargando datos para exportación:", error);
      } finally {
        setLoadingEmpleados(false);
      }
    };
    cargarDatos();
  }, [usuario.ruc, planillaId]);

  useEffect(() => {
    // Pre-selección inteligente para T-Registro
    if (tipoExportacion === 'tregistro' && planilla && empleados.length > 0) {
      const { periodo } = planilla;
      if (periodo) {
        const ops = {};
        // Encontrar quienes entraron o salieron en este periodo
        const automaticos = empleados.filter(emp => {
          const ingreso = parseFecha(emp.fecha_ingreso);
          const cese = parseFecha(emp.fecha_cese);
          const periodoStr = periodo; // YYYY-MM
          const checkIngreso = periodoFromDate(ingreso) === periodoStr;
          const checkCese = periodoFromDate(cese) === periodoStr;
          
          if (checkCese) ops[emp.id] = 'BAJA';
          else if (checkIngreso) ops[emp.id] = 'ALTA';
          
          return checkIngreso || checkCese;
        }).map(emp => emp.id);
        
        setEmpleadosSeleccionados(automaticos);
        setOperacionesTRegistro(ops);
      }
    }
  }, [tipoExportacion, planilla, empleados]);

  const toggleEmpleado = (id) => {
    setEmpleadosSeleccionados(prev => {
      const exists = prev.includes(id);
      if (exists) return prev.filter(eId => eId !== id);
      setOperacionesTRegistro((oldOps) => ({
        ...oldOps,
        [id]: oldOps[id] || 'MODIFICAR',
      }));
      return [...prev, id];
    });
  };

  const handleOperacionEmpleado = (id, operacion) => {
    setOperacionesTRegistro((prev) => ({ ...prev, [id]: operacion }));
    if (operacion !== 'BAJA') {
      setMotivosBajaTRegistro((prev) => {
        if (!Object.prototype.hasOwnProperty.call(prev, id)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const seleccionarTodos = () => {
    if (empleadosSeleccionados.length === empleados.length) {
      setEmpleadosSeleccionados([]);
    } else {
      setEmpleadosSeleccionados(empleados.map(e => e.id));
    }
  };

  const tiposExportacion = {
    plame: {
      titulo: "PLAME (Planilla Mensual de Pagos)",
      descripcion: "Generar archivos para declaración ante SUNAT",
      icono: <FaUniversity size={32} />,
      color: "#dc3545",
      archivos: ["REM", "JOR"],
      entidad: "SUNAT"
    },
    afpnet: {
      titulo: "AFPNET (Aportes AFP)",
      descripcion: "Generar archivo para aportes previsionales",
      icono: <FaBuilding size={32} />,
      color: "#007bff", 
      archivos: ["AFP"],
      entidad: "SBS"
    },
    tregistro: {
      titulo: "T-Registro (Altas, Bajas y Modificaciones)",
      descripcion: "Generar archivos de empleador y trabajadores para SUNAT",
      icono: <FaFileAlt size={32} />,
      color: "#28a745",
      archivos: ["EMP", "TRAB"],
      entidad: "SUNAT"
    },
    interbank: {
      titulo: "Interbank - Pagos Masivos",
      descripcion: "Formato Teleproceso Haberes para pago de planilla",
      icono: <img src="/assets/bancos/logo_interbank.png" alt="Interbank" style={{ width: '50px', height: '50px', borderRadius: '5px' }} />,
      color: "#0032A0",
      archivos: ["TXT"],
      entidad: "INTERBANK"
    },
    bcp: {
      titulo: "BCP - Pagos Masivos",
      descripcion: "Formato Telecrédito Haberes para pago de planilla",
      icono: <img src="/assets/bancos/logo_bcp.png" alt="BCP" style={{ width: '50px', height: '50px', borderRadius: '5px' }} />,
      color: "#FF6600",
      archivos: ["TXT"],
      entidad: "BCP"
    },
    bbva: {
      titulo: "BBVA - Pagos Masivos",
      descripcion: "Formato de Pago de Haberes Cash Management",
      icono: <img src="/assets/bancos/logo_bbva.png" alt="BBVA" style={{ width: '50px', height: '50px', borderRadius: '5px' }} />,
      color: "#1973B8",
      archivos: ["TXT"],
      entidad: "BBVA"
    }
  };

  const handleUploadComprobante = async (e, tipo) => {
    const files = Array.from(e.target.files);
    if (!files.length || !planillaId || !usuario?.ruc) return;

    // Filtrar archivos mayores a 10MB
    const validFiles = files.filter(f => {
      if (f.size > 10 * 1024 * 1024) {
        alert(`El archivo ${f.name} pesa más de 10MB y no fue subido.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    try {
      setUploading(prev => ({ ...prev, [tipo]: 0 }));
      const progressByIndex = {};
      
      const uploadPromises = validFiles.map(async (file, index) => {
        const fileExtension = file.name.split('.').pop();
        const fileName = `${tipo}_${Date.now()}_${index}.${fileExtension}`;
        const storagePath = `comprobantes_planilla/${usuario.ruc}/${planillaId}/${fileName}`;
        const storageRef = ref(storage, storagePath);
        
        // Para subir multiples rápidamente podemos evitar el Resumable o simplemente usar Promise
        const uploadTask = uploadBytesResumable(storageRef, file);
        return new Promise((resolve, reject) => {
            uploadTask.on('state_changed', 
                (snapshot) => {
                  progressByIndex[index] = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                  const currentValues = Object.values(progressByIndex);
                  const averageProgress = currentValues.length
                    ? currentValues.reduce((acc, p) => acc + p, 0) / validFiles.length
                    : 0;
                  setUploading(prev => ({ ...prev, [tipo]: Math.round(averageProgress) }));
                }, 
                (error) => reject(error),
                async () => {
                  const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                  resolve({
                      url: downloadURL,
                      nombre: file.name,
                      fecha: new Date().toISOString(),
                      path: storagePath
                  });
                }
            );
        });
      });

      const uploadedFiles = await Promise.all(uploadPromises);

      // Traer la data existente y asegurar que sea un Array
      const existingData = planilla?.comprobantes?.[tipo];
      const existingArray = Array.isArray(existingData) ? existingData : (existingData ? [existingData] : []);
      
      const nuevosComprobantes = {
        ...(planilla.comprobantes || {}),
        [tipo]: [...existingArray, ...uploadedFiles]
      };

      const planillaRef = doc(db, "pl_planillas", planillaId);
      await updateDoc(planillaRef, { comprobantes: nuevosComprobantes });
      setPlanilla(prev => ({ ...prev, comprobantes: nuevosComprobantes }));

      setUploading(prev => {
        const next = { ...prev };
        delete next[tipo];
        return next;
      });

    } catch (err) {
      console.error("Error en proceso de subida múltiple:", err);
      setUploading(prev => {
        const next = { ...prev };
        delete next[tipo];
        return next;
      });
    }
  };

  const handleDeleteComprobante = async (tipo, idx) => {
    if (!window.confirm("¿Está seguro de eliminar este comprobante?")) return;
    
    try {
      const existingData = planilla?.comprobantes?.[tipo];
      const dataArray = Array.isArray(existingData) ? existingData : (existingData ? [existingData] : []);
      const comprobante = dataArray[idx];

      if (comprobante && comprobante.path) {
        const fileRef = ref(storage, comprobante.path);
        await deleteObject(fileRef);
      }

      dataArray.splice(idx, 1);
      const nuevosComprobantes = { ...planilla.comprobantes };
      
      if (dataArray.length === 0) {
        delete nuevosComprobantes[tipo];
      } else {
        nuevosComprobantes[tipo] = dataArray;
      }

      const planillaRef = doc(db, "pl_planillas", planillaId);
      await updateDoc(planillaRef, { comprobantes: nuevosComprobantes });
      setPlanilla(prev => ({ ...prev, comprobantes: nuevosComprobantes }));
    } catch (err) {
      console.error("Error eliminando comprobante:", err);
    }
  };

  const ComprobanteItem = ({ tipo, titulo, color }) => {
    const rawData = planilla?.comprobantes?.[tipo];
    const dataArray = Array.isArray(rawData) ? rawData : (rawData ? [rawData] : []);
    const isUploading = uploading[tipo] !== undefined;

    return (
      <ListGroup.Item className="border-0 bg-white rounded-3 shadow-sm mb-3 p-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="d-flex align-items-center">
            <div 
              className="rounded-circle p-3 me-3 text-white" 
              style={{ backgroundColor: color }}
            >
              <FaFileInvoiceDollar size={24} />
            </div>
            <div>
              <h6 className="fw-bold mb-0">{titulo}</h6>
              <small className="text-muted">{dataArray.length} archivo(s) subido(s)</small>
            </div>
          </div>
          
          <div className="d-flex gap-2">
            {isUploading ? (
              <div className="text-center" style={{ width: "100px" }}>
                <small className="d-block mb-1">{uploading[tipo]}%</small>
                <ProgressBar now={uploading[tipo]} size="sm" variant="success" style={{ height: "5px" }} />
              </div>
            ) : (
              <label className="btn btn-outline-success btn-sm mb-0 cursor-pointer">
                <FaCloudUploadAlt className="me-1" /> Subir
                <input 
                  type="file" 
                  multiple
                  className="d-none" 
                  accept=".pdf,image/*" 
                  onChange={(e) => handleUploadComprobante(e, tipo)}
                />
              </label>
            )}
          </div>
        </div>

        {/* Lista de archivos subidos para este tipo */}
        {dataArray.length > 0 && (
          <div className="mt-3 border-top pt-2">
            {dataArray.map((docItem, idx) => (
               <div key={idx} className="d-flex justify-content-between align-items-center bg-light p-2 mb-2 rounded border">
                  <div className="text-truncate me-2" style={{ maxWidth: "250px" }} title={docItem.nombre}>
                     <small className="fw-bold d-block text-truncate text-secondary mb-1">{docItem.nombre}</small>
                     <small className="text-black-50">{new Date(docItem.fecha).toLocaleString()}</small>
                  </div>
                  <div className="d-flex gap-2 flex-shrink-0">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      onClick={() => window.open(docItem.url, '_blank')}
                      title="Ver Archivo"
                    >
                      <FaEye />
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm" 
                      onClick={() => handleDeleteComprobante(tipo, idx)}
                      title="Eliminar Archivo"
                    >
                      <FaTrashAlt />
                    </Button>
                  </div>
               </div>
            ))}
          </div>
        )}
      </ListGroup.Item>
    );
  };
  
  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium */}
          <Card.Header
            className="text-white py-4"
            style={{
              background: "linear-gradient(135deg, #8B4513 0%, #A0522D 100%)",
              borderBottom: "3px solid #D2691E",
            }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">
                  <FaFileExport className="me-3" />
                  Exportación de Archivos TXT
                </h2>
                <p className="mb-0 opacity-75">
                  Genere archivos oficiales para declaraciones gubernamentales
                </p>
              </div>
              {planillaId && (
                <Badge 
                  bg="light" 
                  text="dark" 
                  className="px-3 py-2"
                  style={{ fontSize: "0.9rem" }}
                >
                  <FaFileAlt className="me-1" />
                  Planilla ID: {planillaId}
                </Badge>
              )}
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-4 custom-tabs"
              fill
            >
              <Tab 
                eventKey="exportar" 
                title={<span><FaFileExport className="me-2" />1. Exportar Archivos</span>}
              >
                {/* Contenido actual de exportación */}
                <div className="pt-2">
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <h5 className="fw-semibold mb-3" style={{ color: "#8B4513" }}>
                  <FaCog className="me-2" />
                  Seleccione el tipo de exportación
                </h5>
                
                <Row className="g-3">
                  {Object.entries(tiposExportacion).map(([key, config]) => (
                    <Col md={4} key={key}>
                      <Card
                        className={`border-0 shadow-sm cursor-pointer h-100 ${
                          tipoExportacion === key ? 'border-primary' : ''
                        }`}
                        style={{
                          borderRadius: "15px",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          border: tipoExportacion === key ? `3px solid ${config.color}` : "1px solid #e9ecef"
                        }}
                        onClick={() => setTipoExportacion(key)}
                      >
                        <Card.Body className="p-4 text-center">
                          <div 
                            className="mb-3 d-inline-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              width: "80px",
                              height: "80px",
                              background: `linear-gradient(135deg, ${config.color} 0%, ${config.color}dd 100%)`,
                              color: "white"
                            }}
                          >
                            {config.icono}
                          </div>
                          <h6 className="fw-bold mb-2">{config.titulo}</h6>
                          <p className="text-muted small mb-3">{config.descripcion}</p>
                          <div className="d-flex justify-content-center gap-1 mb-2">
                            {config.archivos.map(archivo => (
                              <Badge key={archivo} bg="secondary" className="px-2 py-1">
                                {archivo}
                              </Badge>
                            ))}
                          </div>
                          <small className="text-muted">
                            <FaBuilding className="me-1" />
                            {config.entidad}
                          </small>
                          {tipoExportacion === key && (
                            <div className="mt-2">
                              <FaCheckCircle className="text-success" size={20} />
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Card.Body>
            </Card>
            
            {/* Alertas de validación */}
            {tipoExportacion && !planillaId && (
              <Alert 
                variant="warning" 
                className="d-flex align-items-center mb-4"
                style={{ borderRadius: "15px", border: "none" }}
              >
                <FaExclamationTriangle className="me-2" />
                <div>
                  <strong>Planilla requerida:</strong> Debe acceder a esta página desde una planilla específica 
                  para generar archivos {tiposExportacion[tipoExportacion]?.titulo}.
                </div>
              </Alert>
            )}

            {/* Selección de empleados para T-Registro */}
            {tipoExportacion === 'tregistro' && planillaId && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0" style={{ color: "#8B4513" }}>
                    Seleccionar Trabajadores (Altas / Bajas / Modificaciones)
                    <Badge bg="info" className="ms-2">{empleadosSeleccionados.length} seleccionados</Badge>
                  </h6>
                  <Button variant="link" size="sm" onClick={seleccionarTodos} className="text-decoration-none">
                    {empleadosSeleccionados.length === empleados.length ? "Deseleccionar todos" : "Seleccionar todos"}
                  </Button>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {loadingEmpleados ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" size="sm" className="me-2" />
                        <span className="small text-muted">Cargando trabajadores...</span>
                      </div>
                    ) : (
                      <table className="table table-hover table-sm align-middle mb-0">
                        <thead className="bg-light sticky-top" style={{ zIndex: 10 }}>
                          <tr className="small text-uppercase text-muted">
                            <th className="ps-4 py-3" style={{ width: "50px" }}></th>
                            <th className="py-3">Trabajador</th>
                            <th className="py-3">DNI</th>
                            <th className="py-3 text-center">Estado / Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {empleados.length > 0 ? (
                            empleados.map(emp => {
                              const esAlta = planilla && periodoFromDate(parseFecha(emp.fecha_ingreso)) === planilla.periodo;
                              const esBaja = planilla && periodoFromDate(parseFecha(emp.fecha_cese)) === planilla.periodo;
                              
                              return (
                                <tr key={emp.id} onClick={() => toggleEmpleado(emp.id)} style={{ cursor: "pointer" }}>
                                  <td className="ps-4">
                                    <Form.Check 
                                      type="checkbox"
                                      checked={empleadosSeleccionados.includes(emp.id)}
                                      onChange={() => {}}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td>
                                    <div className="fw-bold small">{emp.nombres} {emp.apellidos}</div>
                                  </td>
                                  <td>
                                    <div className="small text-muted">{emp.dni}</div>
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <Form.Select 
                                        size="sm" 
                                        className="w-auto"
                                        style={{ fontSize: '0.75rem' }}
                                        value={operacionesTRegistro[emp.id] || 'MODIFICAR'}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleOperacionEmpleado(emp.id, e.target.value);
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <option value="ALTA">ALTA</option>
                                        <option value="BAJA">BAJA</option>
                                        <option value="MODIFICAR">MODIFICAR</option>
                                      </Form.Select>
                                      {(operacionesTRegistro[emp.id] || 'MODIFICAR') === 'BAJA' && (
                                        <Form.Select
                                          size="sm"
                                          className="w-auto"
                                          style={{ fontSize: '0.75rem', minWidth: '180px' }}
                                          value={motivosBajaTRegistro[emp.id] || ''}
                                          onChange={(e) => {
                                            e.stopPropagation();
                                            setMotivosBajaTRegistro((prev) => ({ ...prev, [emp.id]: e.target.value }));
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          isInvalid={!motivosBajaTRegistro[emp.id]}
                                        >
                                          <option value="">Motivo de baja...</option>
                                          {MOTIVOS_BAJA_TREGISTRO.map((m) => (
                                            <option key={m.code} value={m.code}>
                                              {m.label}
                                            </option>
                                          ))}
                                        </Form.Select>
                                      )}
                                      {esAlta && <Badge bg="success" style={{ fontSize: '0.65rem' }}>Sugerido: Alta</Badge>}
                                      {esBaja && <Badge bg="danger" style={{ fontSize: '0.65rem' }}>Sugerido: Baja</Badge>}
                                      {!esAlta && !esBaja && (
                                        <Badge bg="secondary" style={{ fontSize: '0.65rem' }}>Sugerido: Modificar</Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center py-4 text-muted small">
                                No se encontraron trabajadores registrados para esta empresa
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Botones de acción */}
            <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={8}>
                    {tipoExportacion && planillaId && (
                      <div>
                        <h6 className="fw-semibold mb-2">
                          Listo para generar: {tiposExportacion[tipoExportacion]?.titulo}
                        </h6>
                        <p className="text-muted mb-0 small">
                          Se generarán los archivos necesarios para la declaración ante {tiposExportacion[tipoExportacion]?.entidad}
                        </p>
                      </div>
                    )}
                  </Col>
                  <Col md={4} className="text-end">
                    <div className="d-flex gap-2 justify-content-end">
                      <Button 
                        variant="outline-secondary"
                        onClick={() => navigate(-1)}
                        style={{ borderRadius: "10px" }}
                      >
                        <FaArrowLeft className="me-1" />
                        Volver
                      </Button>
                      <Button 
                        onClick={() => handleExportacion()}
                        disabled={
                          !tipoExportacion || 
                          loading || 
                          (TIPOS_REQUIEREN_PLANILLA.has(tipoExportacion) && !planillaId)
                        }
                        size="lg"
                        className="px-4 py-2"
                        style={{
                          borderRadius: "15px",
                          background: loading 
                            ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                            : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                          border: "none",
                          fontWeight: "600"
                        }}
                      >
                        {loading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Procesando...
                          </>
                        ) : (
                          <>
                            <FaDownload className="me-2" />
                            Generar Archivos
                          </>
                        )}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Barra de progreso durante carga */}
            {loading && (
              <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-3">
                    <h6 className="fw-semibold">Generando archivos...</h6>
                    <p className="text-muted mb-0">Por favor espere mientras se procesan los datos</p>
                  </div>
                  <ProgressBar 
                    animated 
                    now={100} 
                    style={{ height: "8px", borderRadius: "10px" }}
                  />
                </Card.Body>
              </Card>
            )}
            
            {/* Resultado */}
            {resultado && (
              <Alert 
                variant={resultado.success ? "success" : "danger"} 
                className="d-flex align-items-start"
                style={{ borderRadius: "15px", border: "none" }}
              >
                {resultado.success ? (
                  <FaCheckCircle className="me-2 mt-1" size={20} />
                ) : (
                  <FaExclamationTriangle className="me-2 mt-1" size={20} />
                )}
                <div>
                  <strong>{resultado.success ? "Éxito:" : "Error:"}</strong> {resultado.mensaje}
                  {!resultado.success && Array.isArray(resultado.detalles) && resultado.detalles.length > 0 && (
                    <ul className="mt-2 mb-0 small">
                      {resultado.detalles.map((d, idx) => (
                        <li key={`err-det-${idx}`}>{d}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </Alert>
            )}
            
            {/* Instrucciones */}
            {tipoExportacion && (
              <Card className="shadow-sm border-0" style={{ borderRadius: "15px" }}>
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background: `linear-gradient(135deg, ${tiposExportacion[tipoExportacion].color} 0%, ${tiposExportacion[tipoExportacion].color}dd 100%)`,
                    borderRadius: "15px 15px 0 0"
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaInfoCircle className="me-2" />
                    <span className="fw-semibold">Instrucciones - {tiposExportacion[tipoExportacion].titulo}</span>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  {tipoExportacion === 'afpnet' && (
                    <div>
                      <h6 className="fw-semibold mb-3" style={{ color: "#8B4513" }}>
                        Proceso AFPNET:
                      </h6>
                      <ul className="list-unstyled">
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="primary" className="me-2 mt-1">1</Badge>
                          <div>Los archivos se generarán por cada AFP donde tenga trabajadores afiliados.</div>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="primary" className="me-2 mt-1">2</Badge>
                          <div>Cargue estos archivos en el sistema AFPNET para declarar sus aportes.</div>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="primary" className="me-2 mt-1">3</Badge>
                          <div>El formato cumple con las especificaciones vigentes de la SBS.</div>
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  {tipoExportacion === 'plame' && (
                    <div>
                      <h6 className="fw-semibold mb-3" style={{ color: "#8B4513" }}>
                        Archivos PLAME generados:
                      </h6>
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px" }}>
                            <Card.Body className="p-3 text-center">
                              <Badge bg="danger" className="mb-2 px-3 py-2">REM</Badge>
                              <p className="small mb-0">Remuneraciones e Ingresos</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px" }}>
                            <Card.Body className="p-3 text-center">
                              <Badge bg="warning" className="mb-2 px-3 py-2">JOR</Badge>
                              <p className="small mb-0">Jornadas y Horas</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                      <Alert variant="info" className="mb-0" style={{ borderRadius: "10px", border: "none" }}>
                        <FaInfoCircle className="me-2" />
                        <strong>Siguiente paso:</strong> Importe estos archivos en el PDT-PLAME de SUNAT para completar su declaración mensual.
                      </Alert>
                    </div>
                  )}
                  {tipoExportacion === 'tregistro' && (
                    <div>
                      <h6 className="fw-semibold mb-3" style={{ color: "#8B4513" }}>
                        Proceso T-Registro:
                      </h6>
                      <ul className="list-unstyled mb-4">
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="success" className="me-2 mt-1">1</Badge>
                          <div className="small">Se han seleccionado automáticamente los trabajadores con ingresos (Altas) o ceses (Bajas) en el periodo.</div>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="success" className="me-2 mt-1">2</Badge>
                          <div className="small">Puede ajustar la selección manualmente si necesita declarar otros trabajadores (Ejm: cambios de datos).</div>
                        </li>
                        <li className="d-flex align-items-start mb-2">
                          <Badge bg="success" className="me-2 mt-1">3</Badge>
                          <div className="small">Presione "Generar Archivos" para descargar los TXT y cárguelos en SOL SUNAT.</div>
                        </li>
                      </ul>

                      <h6 className="fw-bold mb-3 small" style={{ color: "#8B4513" }}>
                        Estructura de archivos:
                      </h6>
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px", backgroundColor: "#f8f9fa" }}>
                            <Card.Body className="p-2 text-center">
                              <Badge bg="success" className="mb-1" style={{ fontSize: "0.7rem" }}>RP_EMPLEADOR</Badge>
                              <p className="small mb-0" style={{ fontSize: "0.75rem" }}>Datos del empleador</p>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card className="border-0 shadow-sm h-100" style={{ borderRadius: "10px", backgroundColor: "#f8f9fa" }}>
                            <Card.Body className="p-2 text-center">
                              <Badge bg="success" className="mb-1" style={{ fontSize: "0.7rem" }}>RP_TRABAJADOR</Badge>
                              <p className="small mb-0" style={{ fontSize: "0.75rem" }}>Altas y Bajas</p>
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {tipoExportacion === 'interbank' && (
                    <div>
                      <h6 className="fw-semibold mb-3">Instrucciones Interbank:</h6>
                      <ul className="list-unstyled mb-0">
                        <li className="mb-2 small d-flex align-items-start">
                          <FaCheckCircle className="text-success me-2 mt-1" />
                          <div>Formato de ancho fijo (109 caracteres) para importación masiva.</div>
                        </li>
                        <li className="mb-2 small d-flex align-items-start">
                          <FaCheckCircle className="text-success me-2 mt-1" />
                          <div>Cargue el archivo TXT a través de Interbank Empresas (Teleproceso).</div>
                        </li>
                        <li className="small d-flex align-items-start">
                          <FaCheckCircle className="text-success me-2 mt-1" />
                          <div>Asegúrese de que el saldo de la cuenta origen sea suficiente para el abono total.</div>
                        </li>
                      </ul>
                    </div>
                  )}
                  
                  {(tipoExportacion === 'bcp' || tipoExportacion === 'bbva') && (
                    <div className="text-center py-3">
                      <FaCog className="fa-spin mb-2" size={24} />
                      <p className="mb-0 small text-muted">Módulo de integración bancaria en desarrollo para {tipoExportacion.toUpperCase()}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
                </div>
              </Tab>
              
              <Tab 
                eventKey="comprobantes" 
                title={<span><FaFileInvoiceDollar className="me-2" />2. Comprobantes de Pago</span>}
                disabled={!planillaId}
              >
                <div className="pt-3">
                  <div className="mb-4">
                    <h5 className="fw-bold" style={{ color: "#8B4513" }}>Gestión de Comprobantes de Pago</h5>
                    <p className="text-muted small">Cargue los sustentos de los pagos realizados para esta planilla.</p>
                  </div>

                  <ListGroup className="bg-transparent">
                    <ComprobanteItem 
                      tipo="banco" 
                      titulo="Pago de Haberes (Banco)" 
                      color="#0032A0" 
                    />
                    <ComprobanteItem 
                      tipo="sis" 
                      titulo="Pago SIS (Banco de la Nación)" 
                      color="#28a745" 
                    />
                    <ComprobanteItem 
                      tipo="afp" 
                      titulo="Pago AFPNET (Aportes)" 
                      color="#007bff" 
                    />
                    <ComprobanteItem 
                      tipo="sunat" 
                      titulo="Pago SUNAT (PLAME/Impuestos)" 
                      color="#dc3545" 
                    />
                  </ListGroup>

                  <Alert variant="info" className="mt-4 border-0 shadow-sm" style={{ borderRadius: "12px" }}>
                    <FaInfoCircle className="me-2" size={20} />
                    <small>
                      Los archivos adjuntos se guardan de forma segura en la nube y quedan vinculados a esta planilla permanentemente.
                    </small>
                  </Alert>
                </div>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default ExportacionArchivos;
